import React, { useEffect, Suspense } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import AppRouter from './components/routing/AppRouter';
import ErrorBoundary from './components/error/ErrorBoundary';
import DevTools from './components/DevTools';
import { initializePerformanceOptimizations } from './utils/performance';
import { AccessibilityAudit } from './utils/accessibility';
import './App.css';

// TEST: Hot reloading working ✅

// CSS for screen reader only content
const srOnlyStyles = `
  .sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
`;

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Initialize performance optimizations
    initializePerformanceOptimizations();
    
    // Add screen reader only styles
    const style = document.createElement('style');
    style.textContent = srOnlyStyles;
    document.head.appendChild(style);
    
    // Run accessibility audit in development
    if (process.env.NODE_ENV === 'development') {
      // Delay audit to allow page to fully render
      setTimeout(() => {
        AccessibilityAudit.logAuditResults();
      }, 2000);
    }

    // Set page language based on i18n
    document.documentElement.lang = i18n.language;
    
    return () => {
      // Cleanup styles if needed
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Box
          component="div"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.default',
          }}
        >
          {/* Skip navigation for keyboard users */}
          <a
            href="#main-content"
            className="sr-only"
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              fontSize: '16px',
              backgroundColor: '#000',
              color: '#fff',
              padding: '8px 16px',
              textDecoration: 'none',
              zIndex: 9999,
              transition: 'all 0.3s',
            }}
            onFocus={(e) => {
              const target = e.target as HTMLElement;
              target.style.left = '0';
              target.style.top = '0';
              target.style.width = 'auto';
              target.style.height = 'auto';
            }}
            onBlur={(e) => {
              const target = e.target as HTMLElement;
              target.style.left = '-10000px';
              target.style.top = 'auto';
              target.style.width = '1px';
              target.style.height = '1px';
            }}
            onClick={(e) => {
              e.preventDefault();
              const mainContent = document.getElementById('main-content');
              if (mainContent) {
                mainContent.focus();
                mainContent.scrollIntoView();
              }
            }}
          >
            Skip to main content
          </a>

          {/* ARIA live region for dynamic announcements */}
          <div
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            id="aria-live-region"
          />

          <AppRouter />
          
          {/* Development Tools */}
          <DevTools />
        </Box>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
